import moment from 'moment'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import { Drawer } from '@mui/material'
import Moment from 'react-moment'
import { breakpoints, colors, fontWeights } from '@/styles'
import VerifiedBadge from '@/components/verified-badge'
import client from '@/ports/api-client'
import useMediaQuery from '@/hooks/use-media-query'
import { CloseIcon, DoubleCheckmark, ReadCheckmark } from '@/components/icons'
import Message from './chat-message/bubble-message'

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px 0px;
  width: 440px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
    height: 80vh;
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid ${colors.grey88};
  padding: 24px 30px;
  font-weight: ${fontWeights.bold};
`

const StyledCloseIcon = styled(CloseIcon)`
  margin-right: 10px;
  width: 10px;
  height: 10px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.7;
  }
`

const ChatMessageContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  background-color: ${colors.grey96};
  border-radius: 6px;
  padding: 12px 14px 0px 50px;
`

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px 22px 0px 31px;
`

const ReadContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  background-color: ${colors.blue96};
  border-radius: 6px;
  margin-top: 20px;
  padding: 11px 13px 0px;
  ${props =>
    props.isEmpty &&
    `
    padding-bottom: 25px;
  `}
`

const DetailsTitle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  font-size: 12px;
  padding-left: 5px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${colors.blue88};
`

const DetailsText = styled.span`
  margin-right: 1px;
`

const PersonContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 10px 5px;
  border-bottom: 1px solid ${colors.blue88};
  &:first-child {
    border-bottom: none;
  }
  &:last-child {
    border-bottom: none;
  }
`

const NameText = styled.span`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: ${fontWeights.bold};
  margin-bottom: 0px;
`

const TimeContainer = styled.span`
  font-weight: ${fontWeights.regular};
  font-size: 12px;
  color: ${colors.grey56};
  font-family; Inter;
`

const InvalidText = styled.span`
  font-size: 14px;
  margin-top: 20px;
  font-style: italic;
  color: ${colors.red};
`

const NotAvailable = styled.span`
  font-size: 12px;
  color: #999999;
  padding: 5px;
`

const DeliveredContainer = styled(ReadContainer)``

const MessageDetailsDrawer = ({ isOpen, activeTimelineEvent, close, timelineUsers }) => {
  const { t } = useTranslation()

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  async function getEventData() {
    if (activeTimelineEvent)
      return await client.get(`/timeline/receipts/${activeTimelineEvent?.id}`)
  }
  const { data } = useQuery({
    queryKey: ['timeline-event-details', activeTimelineEvent],
    queryFn: () => getEventData()
  })
  const timelineEventData = data?.[activeTimelineEvent.id]
  const timestampYear = moment(activeTimelineEvent?.created_at).year()

  const calendarStrings = {
    sameDay: `[${t('b.chat.view.today.text', 'Today')}], h:mm A`,
    lastDay: 'ddd, h:mm A',
    lastWeek: 'MMM DD, h:mm A',
    sameElse: now => {
      return timestampYear === now.year() ? 'MMM DD, h:mm A' : 'MMM DD, YYYY, h:mm A'
    }
  }

  const notAvailable =
    timelineEventData?.read?.length < 1 && timelineEventData?.delivered?.length < 1

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      variant="temporary"
      open={isOpen}
      onClose={close}
    >
      <ContentInner>
        <TitleContainer>
          <StyledCloseIcon onClick={close} />
          <span>{t('b.chat.view.message.title', 'Message')}</span>
        </TitleContainer>
        <DetailsContainer>
          {activeTimelineEvent && (
            <ChatMessageContainer>
              <Message
                pointer={false}
                isDrawerMessage
                showDrawer={() => {}}
                isRead={activeTimelineEvent.is_read}
                key={activeTimelineEvent.id}
                event={activeTimelineEvent}
                position={'right'}
                showName={false}
                isFirst={true}
                previousMessage={null}
                nextMessage={null}
              />
            </ChatMessageContainer>
          )}
          {notAvailable && (
            <InvalidText>
              {t(
                'b.chat.view.invalid_message.text',
                'Delivery and read receipts not available for this message'
              )}
            </InvalidText>
          )}
          <ReadContainer isEmpty={!timelineEventData?.read}>
            <DetailsTitle>
              <DetailsText>{t('b.chat.view.read_by.text', 'Read By')}</DetailsText>
              <ReadCheckmark />
            </DetailsTitle>
            {notAvailable ? (
              <NotAvailable>{t('b.chat.view.not_available.text', 'Not Available')}</NotAvailable>
            ) : (
              timelineEventData?.read?.map((receipt, index) => (
                <PersonContainer key={`read-${receipt.id}`}>
                  <NameText>
                    {receipt.preferred_name}
                    <VerifiedBadge
                      verified={timelineUsers[receipt.user_id]?.liv_verified_state === 'verified'}
                      type="id"
                      width={18}
                      height={18}
                      left={2}
                    />
                  </NameText>
                  <TimeContainer>
                    <Moment calendar={calendarStrings} local>{`${receipt.read_date}-0000`}</Moment>
                  </TimeContainer>
                </PersonContainer>
              ))
            )}
          </ReadContainer>

          {notAvailable ? (
            <DeliveredContainer isEmpty={!timelineEventData?.delivered}>
              <DetailsTitle>
                <DetailsText>{t('b.chat.view.delivered_to.text', 'Delivered To')}</DetailsText>
                <DoubleCheckmark />
              </DetailsTitle>
              <NotAvailable>{t('b.chat.view.not_available.text', 'Not Available')}</NotAvailable>
            </DeliveredContainer>
          ) : (
            timelineEventData?.delivered?.length > 0 && (
              <DeliveredContainer>
                <DetailsTitle>
                  <DetailsText>{t('b.chat.view.delivered_to.text', 'Delivered To')}</DetailsText>
                  <DoubleCheckmark />
                </DetailsTitle>
                {timelineEventData?.delivered?.map((receipt, index) => (
                  <PersonContainer key={`delivered-${receipt.id}`}>
                    <NameText>
                      {receipt.preferred_name}
                      <VerifiedBadge
                        verified={timelineUsers[receipt.user_id]?.liv_verified_state === 'verified'}
                        type="id"
                        width={18}
                        height={18}
                        left={2}
                      />
                    </NameText>
                    <TimeContainer>
                      <Moment
                        calendar={calendarStrings}
                        local
                      >{`${receipt.delivery_date}-0000`}</Moment>
                    </TimeContainer>
                  </PersonContainer>
                ))}
              </DeliveredContainer>
            )
          )}
        </DetailsContainer>
      </ContentInner>
    </Drawer>
  )
}

export default MessageDetailsDrawer
